import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Circle, Loader2, XCircle, Sparkles, AlertTriangle, RotateCcw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { errMsg } from "@/lib/api";
import { useEditor } from "./useEditorState";
import GenerateCostDialog from "@/components/ai/GenerateCostDialog";
import StopButton from "@/components/ai/StopButton";

const ICON = { done: [CheckCircle2, "text-emerald-600"], running: [Loader2, "text-sky-600 animate-spin"], error: [XCircle, "text-red-600"], pending: [Circle, "text-slate-300"] };

function useGenerationActions() {
  const ed = useEditor();
  const [body, setBody] = useState(null);
  const started = (data) => { setBody(null); ed.setProject((p) => ({ ...p, generation: { status: "running", job_id: data.job_id } })); };
  const dismiss = async () => {
    ed.setProject((p) => ({ ...p, generation: { ...p.generation, dismissed: true } }));
    try { await ed.api.post(`/projects/${ed.id}/generation/dismiss`); } catch { /* local dismiss is enough */ }
  };
  const dialog = (
    <GenerateCostDialog open={!!body} onOpenChange={(o) => !o && setBody(null)} api={ed.api} projectId={ed.id} body={body} onStarted={started}
      onBeforeStart={body?.resume ? null : ed.save} title={body?.resume ? "Riprendere la generazione?" : "Rigenerare da zero?"}
      description={body?.resume ? "Vengono generate solo le parti mancanti: quelle già completate restano come sono." : "Il contenuto attuale viene salvato in una versione e sostituito dalla nuova generazione."} />
  );
  return { resume: () => setBody({ resume: true }), restart: () => setBody({ confirm_overwrite: true }), dismiss, dialog };
}

function Banner({ tone, title, children, testid }) {
  const err = tone === "error";
  return (
    <div className={`absolute bottom-4 left-1/2 -translate-x-1/2 z-20 w-[min(600px,94%)] rounded-xl border bg-white shadow-lg p-4 flex gap-3 ${err ? "border-red-200" : "border-amber-200"}`} data-testid={testid}>
      <AlertTriangle className={`shrink-0 ${err ? "text-red-600" : "text-amber-600"}`} size={18} />
      <div className="flex-1 min-w-0 text-sm"><p className="font-medium text-slate-900">{title}</p>{children}</div>
    </div>
  );
}

function ErrorBanner({ gen }) {
  const a = useGenerationActions();
  if (gen.kind === "duplicate") {
    return (
      <Banner tone="error" title={gen.cancelled ? "Adattamento AI interrotto" : "Adattamento AI non completato"} testid="generation-error">
        <p className="text-slate-600 mt-0.5 break-words" data-testid="generation-error-message">{gen.error}</p>
        <div className="mt-3"><Button size="sm" variant="ghost" onClick={a.dismiss} data-testid="generation-dismiss-btn">Continua manualmente</Button></div>
      </Banner>
    );
  }
  return (
    <Banner tone="error" title={gen.cancelled ? "Generazione interrotta" : "Generazione non completata"} testid="generation-error">
      <p className="text-slate-600 mt-0.5 break-words" data-testid="generation-error-message">{gen.error}</p>
      {!/conservat/i.test(gen.error || "") && <p className="text-[11px] text-slate-500 mt-1">Il progetto, il brief e i contenuti già creati sono stati conservati.</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        {gen.can_resume
          ? <><Button size="sm" onClick={a.resume} data-testid="generation-resume-btn"><RotateCcw size={14} className="mr-1.5" />Riprendi da dove si è interrotta</Button>
            <Button size="sm" variant="outline" onClick={a.restart} data-testid="generation-retry-btn">Rigenera da zero</Button></>
          : <Button size="sm" onClick={a.restart} data-testid="generation-retry-btn">Riprova</Button>}
        <Button size="sm" variant="ghost" onClick={a.dismiss} data-testid="generation-dismiss-btn">Continua manualmente</Button>
      </div>
      {a.dialog}
    </Banner>
  );
}

function ClarifyCard({ gen }) {
  const ed = useEditor();
  const [est, setEst] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const q = gen.question || {};
  useEffect(() => { ed.api.post(`/projects/${ed.id}/generate/estimate`, { resume: true, ai_images: !!gen.ai_images }).then(({ data }) => setEst(data.estimate)).catch(() => {}); }, [ed.api, ed.id, gen.ai_images]);
  const go = async (answer) => {
    setBusy(true);
    try { await ed.api.post(`/projects/${ed.id}/generation/answer`, { answer, note, confirm_usd: est?.max_usd ?? est?.usd }); await ed.load(); toast.success("Ripresa dal punto in cui si era fermata"); }
    catch (e) { toast.error(errMsg(e)); await ed.load(); } finally { setBusy(false); }
  };
  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/80 p-4 backdrop-blur-sm" data-testid="clarify-card">
      <div className="w-full max-w-lg space-y-3 rounded-xl border border-amber-200 bg-white p-5 shadow-lg">
        <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Generazione in pausa · serve un chiarimento</p>
        <p className="text-base font-semibold text-slate-900" data-testid="clarify-question">{q.text}</p>
        {q.reason && <p className="text-xs text-slate-500">Perché: {q.reason}. La struttura del sito è già pronta: riprende da qui senza ripagare le fasi completate.</p>}
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Nota facoltativa per le pagine (es. «niente prezzi in home»)" className="w-full rounded-md border border-slate-200 p-2 text-sm" data-testid="clarify-note" />
        <div className="flex flex-col gap-2">{(q.options || []).map((o, i) => (
          <Button key={o} variant={o.startsWith("Non lo so") ? "outline" : "default"} disabled={busy || gen.status === "resuming"} onClick={() => go(o)} data-testid={`clarify-option-${i}`}>{o.startsWith("Non lo so") ? "Continua senza rispondere (resta DA DEFINIRE)" : o}</Button>))}</div>
        <p className="text-[11px] text-slate-500" data-testid="clarify-estimate">{est ? `Ripresa: stima ${Number(est.usd).toFixed(2)} $ (massimo ${Number(est.max_usd ?? est.usd).toFixed(2)} $), solo per pagine e immagini ancora da fare.` : "Calcolo del costo della ripresa…"}</p>
      </div>
    </div>);
}

function ReopenPill({ gen }) {
  const ed = useEditor();
  const n = (gen.warnings?.length || 0) + (gen.missing_pages?.length || 0);
  const reopen = async () => {
    ed.setProject((p) => ({ ...p, generation: { ...p.generation, dismissed: false } }));
    try { await ed.api.post(`/projects/${ed.id}/generation/reopen`); } catch { /* shown locally anyway */ }
  };
  return (
    <button onClick={reopen} className="absolute bottom-4 left-4 z-20 flex items-center gap-1.5 rounded-full border border-amber-200 bg-white px-3 py-1.5 text-xs text-amber-800 shadow transition-colors hover:bg-amber-50" data-testid="generation-reopen-btn">
      <AlertTriangle size={13} />{gen.status === "error" ? "Esito dell'ultima generazione" : `Avvisi dell'ultima generazione${n ? ` (${n})` : ""}`}
    </button>
  );
}

function WarningsBanner({ gen }) {
  const a = useGenerationActions();
  const [open, setOpen] = useState(false);
  const w = gen.warnings || [];
  const missing = gen.missing_pages || [];
  return (
    <Banner tone="warn" title={missing.length ? `Demo generata: ${missing.length === 1 ? "1 pagina da completare" : `${missing.length} pagine da completare`}` : gen.kind === "duplicate" ? "Copia adattata con alcuni avvisi" : "Demo generata con alcuni avvisi"} testid="generation-warnings">
      <ul className={`mt-1 space-y-1 text-xs text-slate-600 ${open ? "max-h-48 overflow-auto" : ""}`} data-testid="generation-warnings-list">
        {(open ? w : w.slice(0, 2)).map((x, i) => <li key={i} className="break-words">• {x}</li>)}
      </ul>
      {w.length > 2 && <button className="mt-1 text-xs text-sky-700" onClick={() => setOpen(!open)} data-testid="generation-warnings-toggle">{open ? "Mostra meno" : `Mostra tutti (${w.length})`}</button>}
      <div className="mt-3 flex flex-wrap gap-2">
        {missing.length > 0 && <Button size="sm" onClick={a.resume} data-testid="generation-resume-btn"><RotateCcw size={14} className="mr-1.5" />Rigenera pagine mancanti</Button>}
        <Button size="sm" variant="ghost" onClick={a.dismiss} data-testid="generation-dismiss-btn"><X size={14} className="mr-1" />Chiudi</Button>
      </div>
      {a.dialog}
    </Banner>
  );
}

function Progress({ steps, kind, mode }) {
  const ed = useEditor();
  const dup = kind === "duplicate";
  return (
    <div className="absolute inset-0 z-20 bg-slate-50/90 backdrop-blur-sm flex items-center justify-center p-6" data-testid="generation-overlay">
      <div className="w-full max-w-md bg-white rounded-2xl border shadow-xl p-7">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-navy-900 flex items-center justify-center"><Sparkles size={18} className="text-sky-400" /></div>
          <div><p className="font-semibold text-slate-900" data-testid="generation-overlay-title">{dup ? (mode === "new_sector" ? "Adattamento al nuovo settore in corso" : "Duplicazione intelligente in corso") : "Generazione della demo in corso"}</p><p className="text-xs text-slate-500">{dup && mode !== "new_sector" ? "Di solito 1-3 minuti" : "Di solito 3-8 minuti"}. Puoi lasciare questa pagina: continua sul server e ogni parte completata viene salvata.</p></div>
        </div>
        <ul className="mt-6 space-y-2.5 max-h-[50vh] overflow-auto" data-testid="generation-steps">
          {(steps.length ? steps : [{ key: "init", label: "Avvio…", status: "running" }]).map((s) => {
            const [Icon, cls] = ICON[s.status] || ICON.pending;
            return <li key={s.key} className="flex items-center gap-2.5 text-sm text-slate-700" data-testid={`generation-step-${s.key}`}><Icon size={16} className={`shrink-0 ${cls}`} />{s.label}</li>;
          })}
        </ul>
        <div className="mt-6 pt-4 border-t flex flex-wrap items-center justify-between gap-3">
          <p className="text-[11px] text-slate-500 max-w-[16rem]">Interrompendo non parte nessuna nuova chiamata AI e le parti già completate restano salvate.</p>
          <StopButton api={ed.api} projectId={ed.id} label={dup ? "Interrompi adattamento" : "Interrompi generazione"} testid="generation-stop-btn" />
        </div>
      </div>
    </div>
  );
}

export default function GenerationOverlay() {
  const ed = useEditor();
  let gen = ed.project.generation;
  const [job, setJob] = useState(null);
  const running = gen?.status === "running";

  useEffect(() => {
    if (!running || !gen.job_id) return undefined;
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await ed.api.get(`/jobs/${gen.job_id}`);
        if (stop) return;
        setJob(data);
        if (data.status !== "running") {
          await ed.load();
          const dup = gen.kind === "duplicate";
          if (data.status === "done") toast.success(data.result?.warnings?.length ? (dup ? "Copia adattata con alcuni avvisi" : "Demo generata con alcuni avvisi") : (dup ? "Copia adattata al nuovo cliente: ora puoi rifinirla" : "Demo generata: ora puoi rifinirla"));
          else if (data.status === "waiting") toast.info("Generazione in pausa: serve un tuo chiarimento");
          else toast.error(dup ? "Adattamento AI non completato" : "Generazione non completata");
          return;
        }
      } catch { /* retry */ }
      if (!stop) setTimeout(tick, 2500);
    };
    tick();
    return () => { stop = true; };
  }, [running, gen?.job_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (running) return <Progress steps={job?.steps || []} kind={gen.kind} mode={gen.mode} />;
  if (!gen) return null;
  if (!gen.status && gen.report?.status) gen = { ...gen, status: gen.report.status, warnings: gen.warnings || gen.report.warnings };  // older layouts keep status/warnings in the report
  if (gen.status === "waiting" || gen.status === "resuming") return <ClarifyCard gen={gen} />;
  if (gen.dismissed) return (gen.warnings?.length || gen.missing_pages?.length || gen.status === "error") ? <ReopenPill gen={gen} /> : null;
  if (gen.status === "error") return <ErrorBanner gen={gen} />;
  if (gen.status === "done" && (gen.warnings?.length || gen.missing_pages?.length)) return <WarningsBanner gen={gen} />;
  return null;
}
